import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import { FiCalendar, FiDownload, FiX } from 'react-icons/fi';
import {
  FaMotorcycle,
  FaCheck,
  FaTimes,
  FaBan,
  FaUtensils,
  FaExchangeAlt,
  FaPizzaSlice,
} from 'react-icons/fa';
import { MdStore } from 'react-icons/md';
import { rgba } from 'polished';
import { format } from 'date-fns';
import pt_BR from 'date-fns/locale/pt-BR';

import { BiGridAlt } from 'react-icons/bi';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import Card from '../../components/Card';
import ProgressBar from '../../components/ProgressBar';
import ActivityIndicator from '../../components/ActivityIndicator';
import Help from './Help';
import ProductItem from './ProductItem';
import PizzaGroupItem from './PizzaGroupItem';
import PizzaDetailsModal, {
  PizzaDetailsModalHandles,
} from './PizzaDetailsModal';
import { ModalHandles } from './Modal';

import BRLMoneyFormat from '../../utils/BRLMoneyFormat';
import handleNumberFormat from '../../utils/handleNumberFormat';
import getDateKeyInTimeZone, {
  getValidTimeZone,
} from '../../utils/companyDate';
import colors from '../../utils/colors';
import {
  dateFromDateKey,
  getDateKeyDistance,
  getLocalDateKey,
} from './dashboardDetailsPeriod';
import {
  buildCouponsChartData,
  buildSalesChartData,
  DashboardDetailsCoupons,
  DashboardDetailsGranularity,
  DashboardDetailsSeries,
  hasAmbiguousLegacySalesBuckets,
} from './dashboardDetailsCharts';
import {
  mergePizzaSummaries,
  PizzaSummary,
  pizzaGroupTotals,
} from './dashboardDetailsPizzas';
import { productLineTotal } from './productLineTotal';
import {
  buildDashboardDetailsFileName,
  buildDashboardDetailsSheets,
  exportDashboardDetailsWorkbook,
} from './dashboardDetailsExport';

import noImage from '../../assets/no_image.png';
import IfoodImage from '../../assets/ifood.png';
import AtacImage from '../../assets/atac.png';
import PDVImage from '../../assets/pdv.svg';

import {
  Container,
  Wrapper,
  Cards,
  SmallCard,
  CardItem,
  LoadingContainer,
  Calendar,
  Modal,
} from './styles';

type IFilter = 'ifood' | 'atac' | 'pdv';
type ExternalSourceStatus = 'not_selected' | 'not_configured' | 'error' | 'available';

interface DetailsSourceStatus {
  ifood: ExternalSourceStatus;
  pdv: ExternalSourceStatus;
}

const EMPTY_SALES: DashboardDetailsSeries = {
  salesOfDay: [],
  salesOfMonth: [],
  salesOfYear: [],
};

const EMPTY_PIZZAS: PizzaSummary = { sizes: [], flavors: [] };

const EMPTY_COUPONS: DashboardDetailsCoupons = {
  couponsOfDay: [],
  couponsOfMonth: [],
  couponsOfYear: [],
};

const DashboardDetails: React.FC = () => {
  const complementsModalRef = useRef<ModalHandles>(null);
  const pizzaModalRef = useRef<PizzaDetailsModalHandles>(null);
  const theme = useTheme();

  const { addToast } = useToast();
  const { user, rotinas, findRotinas } = useAuth();
  const companyTimeZone = getValidTimeZone(user?.empresa?.time_zone);
  const initialDate = dateFromDateKey(
    getDateKeyInTimeZone(new Date(), companyTimeZone),
  );

  const history = useHistory();
  const detailsRequestRef = useRef(0);
  const complementsRequestRef = useRef(0);

  const [filters, setFilters] = useState<IFilter[]>(['ifood', 'atac', 'pdv']);
  const [showCalendar, setShowCalendar] = useState(false);
  const [productFilter, setProductFilter] = useState('quantity');
  const [salesFilter, setSalesFilter] = useState<DashboardDetailsGranularity>(
    'hour',
  );
  const [couponFilter, setCouponFilter] = useState<DashboardDetailsGranularity>(
    'hour',
  );
  const [startDate, setStartDate] = useState<Date>(initialDate);
  const [endDate, setEndDate] = useState<Date>(initialDate);
  const [sections, setSections] = useState({
    deliveredOrders: {
      amountOfOrders: 0,
      totalValue: 0,
      totalItems: 0,
      totalDelivery: 0,
    },
    withdrawnOrders: { amountOfOrders: 0, totalValue: 0 },
    total: 0,
    totalWithDelivery: 0,
    count: 0,
  });
  const [stages, setStages] = useState({
    amountOfCanceledOrders: 0,
    amountOfDeliveredOrders: 0,
    amountOfDeniedOrders: 0,
    total: 0,
  });
  const [products, setProducts] = useState<any[]>([]);
  const [pizzas, setPizzas] = useState<PizzaSummary>(EMPTY_PIZZAS);
  const [groups, setGroups] = useState<any[]>([]);
  const [complements, setComplements] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [countProducts, setCountProducts] = useState(0);
  const [totalPaymentMethods, setTotalPaymentMethods] = useState(0);
  const [countPaymentMethods, setCountPaymentMethods] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [sourceStatus, setSourceStatus] = useState<DetailsSourceStatus>();
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [pdvSales, setPdvSales] = useState<DashboardDetailsSeries>(EMPTY_SALES);
  const [ifoodSales, setIfoodSales] =
    useState<DashboardDetailsSeries>(EMPTY_SALES);
  const [sales, setSales] = useState<DashboardDetailsSeries>(EMPTY_SALES);
  const [coupons, setCoupons] =
    useState<DashboardDetailsCoupons>(EMPTY_COUPONS);
  const [productComplements, setProductComplements] = useState<{
    complements?: any[];
    flavors?: any[];
    grids?: any[];
  }>({});
  const [loadingComplements, setLoadingComplements] = useState(false);
  const [showProducts, setShowProducts] = useState(true);

  const handleFetchProductComplements = useCallback(
    async (search: number | string, grid: boolean) => {
      const requestId = complementsRequestRef.current + 1;
      complementsRequestRef.current = requestId;
      complementsModalRef.current?.open();

      setProductComplements({});
      setLoadingComplements(true);

      try {
        const response = await api.get(`/dashboard/details/product/${search}`, {
          params: {
            startDate: getLocalDateKey(startDate),
            endDate: getLocalDateKey(endDate),
            companyId: user.empresa_id,
            size: isNaN(Number(search)) ? search : undefined,
            grid,
          },
        });

        if (requestId !== complementsRequestRef.current) {
          return;
        }

        setProductComplements(response.data);
      } catch (err) {
        if (requestId === complementsRequestRef.current) {
          addToast({
            type: 'error',
            title: 'Erro ao obter os complementos do produto',
          });
        }
      } finally {
        if (requestId === complementsRequestRef.current) {
          setLoadingComplements(false);
        }
      }
    },
    [user, startDate, endDate, addToast],
  );

  const handleCloseComplementsModal = useCallback((): void => {
    complementsRequestRef.current += 1;
    setLoadingComplements(false);
    complementsModalRef.current?.close();
  }, []);

  useEffect(() => {
    const requestId = detailsRequestRef.current + 1;
    detailsRequestRef.current = requestId;
    let isActive = true;

    const isCurrentRequest = (): boolean =>
      isActive && detailsRequestRef.current === requestId;

    async function loadData(): Promise<void> {
      setLoading(true);
      setLoadError(false);
      setSourceStatus(undefined);

      try {
        const response = await api.get('/dashboard/details', {
          params: {
            startDate: getLocalDateKey(startDate),
            endDate: getLocalDateKey(endDate),
            companyId: user.empresa_id,
            filters: filters.join(','),
          },
        });

        if (!isCurrentRequest()) {
          return;
        }

        const {
          amountOfCanceledOrders,
          amountOfDeliveredOrders,
          amountOfDeniedOrders,
          deliveredOrders,
          withdrawnOrders,
          ifood,
          pdv,
          products: fetchedProducts,
          paymentMethods: fetchedPaymentMethods,
          sales: fetchedSales,
          groups: fetchedGroups,
          complements: fetchedComplements,
          coupons: fetchedCoupons,
        } = response.data;

        const filteredProducts = fetchedProducts.filter((product: any) => {
          if (response.data.pizzas && product.pizza) return false;
          if (product.produto_controla_grade === 2) {
            return Boolean(product.pizza);
          }

          return true;
        });

        const concatenatedGroups: Map<string, number> = new Map();

        (pdv?.categories || []).forEach((category: any) => {
          const count = concatenatedGroups.get(category.descricao) ?? 0;

          concatenatedGroups.set(category.descricao, count + Number(category.total));
        });

        fetchedGroups.forEach((group: any) => {
          const count = concatenatedGroups.get(group.descricao) ?? 0;

          concatenatedGroups.set(group.descricao, count + Number(group.total));
        });

        setGroups(Array.from(concatenatedGroups.entries()).map(([key, value]) => ({
          descricao: key,
          total: value,
        })));
        setComplements(fetchedComplements);
        setSales(fetchedSales);
        setIfoodSales(ifood?.sales?.sales || EMPTY_SALES);
        setPdvSales(pdv?.sales?.sales || EMPTY_SALES);
        setSourceStatus(response.data.sourceStatus);
        setCoupons(fetchedCoupons);

        const pdvAmountOfCanceledOrders =
          pdv?.statusCount?.find(
            (status: any) => status.status === 9 || status.status === 10,
          )?.quantity ?? 0;
        const pdvAmountOfDeliveredOrders =
          pdv?.statusCount?.find((status: any) => status.status === 6)
            ?.quantity ?? 0;
        const pdvAmountOfDeniedOrders =
          pdv?.statusCount?.find((status: any) => status.status === 2)
            ?.quantity ?? 0;

        setStages({
          amountOfCanceledOrders:
            pdvAmountOfCanceledOrders +
            (ifood?.amountOfCanceledOrders ?? 0) +
            amountOfCanceledOrders,
          amountOfDeliveredOrders:
            pdvAmountOfDeliveredOrders +
            (ifood?.amountOfDeliveredOrders ?? 0) +
            amountOfDeliveredOrders,
          amountOfDeniedOrders:
            pdvAmountOfDeniedOrders +
            (ifood?.amountOfDeniedOrders ?? 0) +
            amountOfDeniedOrders,
          total:
            pdvAmountOfCanceledOrders +
            (ifood?.amountOfCanceledOrders ?? 0) +
            amountOfCanceledOrders +
            pdvAmountOfDeliveredOrders +
            (ifood?.amountOfDeliveredOrders ?? 0) +
            amountOfDeliveredOrders +
            pdvAmountOfDeniedOrders +
            (ifood?.amountOfDeniedOrders ?? 0) +
            amountOfDeniedOrders,
        });
        setSections({
          deliveredOrders: {
            amountOfOrders:
              (ifood?.deliveredOrders[0]?.amountOfOrders ?? 0) +
              deliveredOrders.amountOfOrders,
            totalDelivery:
              (ifood?.deliveredOrders[0]?.totalDelivery ?? 0) +
              deliveredOrders.totalDelivery,
            totalItems:
              (ifood?.deliveredOrders[0]?.totalItems ?? 0) +
              deliveredOrders.totalItems,
            totalValue:
              (ifood?.deliveredOrders[0]?.totalValue ?? 0) +
              deliveredOrders.totalValue,
          },
          withdrawnOrders: {
            amountOfOrders:
              (ifood?.withdrawnOrders[0]?.amountOfOrders ?? 0) +
              (pdv?.withdrawnOrders[0]?.amountOfOrders ?? 0) +
              withdrawnOrders.amountOfOrders,
            totalValue:
              (ifood?.withdrawnOrders[0]?.totalValue ?? 0) +
              (pdv?.withdrawnOrders[0]?.totalValue ?? 0) +
              withdrawnOrders.totalValue,
          },
          total:
            (ifood?.deliveredOrders[0]?.totalItems ?? 0) +
            deliveredOrders.totalItems +
            (ifood?.withdrawnOrders[0]?.totalValue ?? 0) +
            (pdv?.withdrawnOrders[0]?.totalValue ?? 0) +
            withdrawnOrders.totalValue,
          count:
            (ifood?.deliveredOrders[0]?.amountOfOrders ?? 0) +
            deliveredOrders.amountOfOrders +
            (ifood?.withdrawnOrders[0]?.amountOfOrders ?? 0) +
            (pdv?.withdrawnOrders[0]?.amountOfOrders ?? 0) +
            withdrawnOrders.amountOfOrders,
          totalWithDelivery:
            (ifood?.deliveredOrders[0]?.totalValue ?? 0) +
            deliveredOrders.totalValue +
            (ifood?.withdrawnOrders[0]?.totalValue ?? 0) +
            (pdv?.withdrawnOrders[0]?.totalValue ?? 0) +
            withdrawnOrders.totalValue,
        });
        const pizzaSummary = mergePizzaSummaries(
          response.data.pizzas,
          pdv?.pizzas,
        );
        const pizzaTotals = pizzaGroupTotals(pizzaSummary);
        setPizzas(pizzaSummary);
        setProducts([
          ...filteredProducts.map((product: any) => ({
            ...product,
            ifood: false,
            pdv: false,
          })),
          ...(ifood?.products || []).map((product: any) => ({
            ...product,
            ifood: true,
            pdv: false,
          })),
          ...(pdv?.products || []).map((product: any) => ({
            produto_id: product._id,
            ifood: true,
            pdv: true,
            image: product.image,
            name: product.name,
            total: product.total,
            quantity: product.quantity,
          })),
        ]);
        setTotalProducts(
          filteredProducts.reduce(
            (previousValue: number, currentValue: any) =>
              previousValue + productLineTotal(currentValue),
            0,
          ) +
            (ifood?.products || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.total),
              0,
            ) +
            (pdv?.products || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + productLineTotal({ ...currentValue, pdv: true }),
              0,
            ) +
            pizzaTotals.total,
        );
        setCountProducts(
          filteredProducts.reduce(
            (previousValue: number, currentValue: any) =>
              previousValue +
              (Number(currentValue.quantity).toString().indexOf('.') === -1
                ? Number(currentValue.quantity)
                : Number(currentValue.count)),
            0,
          ) +
            (ifood?.products || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.quantity),
              0,
            ) +
            (pdv?.products || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.quantity),
              0,
            ) +
            pizzaTotals.quantity,
        );
        setPaymentMethods([
          ...fetchedPaymentMethods.map((paymentMethod: any) => ({
            ...paymentMethod,
            ifood: false,
          })),
          ...(ifood?.paymentMethods || []).map((paymentMethod: any) => ({
            ...paymentMethod,
            ifood: true,
          })),
          ...(pdv?.paymentMethods || []).map((paymentMethod: any) => ({
            forma_pagamento_empresa_id: String(Math.random()),
            count: paymentMethod.quantity,
            forma_pagamento_descricao: paymentMethod.name,
            forma_pagamento_imagem: 'f01853a78acf3293b82e-mastercard.png',
            sum: paymentMethod.total,
            ifood: false,
          })),
        ]);
        setTotalPaymentMethods(
          fetchedPaymentMethods.reduce(
            (previousValue: number, currentValue: any) =>
              previousValue + Number(currentValue.sum),
            0,
          ) +
            (ifood?.paymentMethods || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.total),
              0,
            ) +
            (pdv?.paymentMethods || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.total),
              0,
            ),
        );
        setCountPaymentMethods(
          fetchedPaymentMethods.reduce(
            (previousValue: number, currentValue: any) =>
              previousValue + Number(currentValue.count),
            0,
          ) +
            (ifood?.paymentMethods || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.quantity),
              0,
            ) +
            (pdv?.paymentMethods || []).reduce(
              (previousValue: number, currentValue: any) =>
                previousValue + Number(currentValue.quantity),
              0,
            ),
        );
      } catch (err) {
        if (isCurrentRequest()) {
          setLoadError(true);
          addToast({
            title: 'Ops...',
            description: 'Ocorreu um erro ao obter os dados.',
            type: 'error',
          });
        }
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isActive = false;
    };
  }, [filters, startDate, endDate, user.empresa_id, companyTimeZone, addToast]);

  useEffect(() => {
    const differenceBetweenDays = getDateKeyDistance(
      getLocalDateKey(startDate),
      getLocalDateKey(endDate),
    );

    if (differenceBetweenDays === 0) {
      setSalesFilter('hour');
      setCouponFilter('hour');

      return;
    }

    if (differenceBetweenDays < 31) {
      setSalesFilter('day');
      setCouponFilter('day');

      return;
    }

    setSalesFilter('month');
    setCouponFilter('month');
  }, [startDate, endDate]);

  const handleChangeDate = useCallback(
    (selectedStartDate: Date, selectedEndDate: Date) => {
      setStartDate(selectedStartDate);
      setEndDate(selectedEndDate);
      setShowCalendar(false);
    },
    [],
  );

  const handleChangeProductsCard = useCallback(() => {
    setShowProducts((oldState) => !oldState);
  }, []);

  function handleCouponsChartData(): [
    string[],
    { label: string; data: number[]; backgroundColor: string }[],
  ] {
    const chart = buildCouponsChartData(
      coupons,
      couponFilter,
      getLocalDateKey(startDate),
      getLocalDateKey(endDate),
    );

    return [
      chart.labels,
      chart.datasets.map((dataset, index) => ({
        ...dataset,
        backgroundColor: colors[index % colors.length],
      })),
    ];
  }

  function handleChartData(): [string[], any] {
    const chart = buildSalesChartData(
      sales,
      ifoodSales,
      pdvSales,
      salesFilter,
      getLocalDateKey(startDate),
      getLocalDateKey(endDate),
    );
    const colorBySource: Record<string, string> = {
      Atac: theme.colors.main,
      iFood: theme.colors.red,
      'Atac PDV': theme.colors.green,
    };

    return [
      chart.labels,
      chart.datasets
        .filter((dataset) =>
          filters.includes(
            dataset.label === 'Atac'
              ? 'atac'
              : dataset.label === 'iFood'
                ? 'ifood'
                : 'pdv',
          ),
        )
        .map((dataset) => ({
          ...dataset,
          borderRadius: 5,
          backgroundColor: rgba(colorBySource[dataset.label], 0.5),
          borderWidth: 2,
          borderSkipped: false,
          borderColor: colorBySource[dataset.label],
        })),
    ];
  }

  const hasAmbiguousExternalBuckets =
    hasAmbiguousLegacySalesBuckets(
      ifoodSales,
      salesFilter,
      getLocalDateKey(startDate),
      getLocalDateKey(endDate),
    ) ||
    hasAmbiguousLegacySalesBuckets(
      pdvSales,
      salesFilter,
      getLocalDateKey(startDate),
      getLocalDateKey(endDate),
    );
  const handleImage = useCallback((image?: string) => {
    if (image) {
      return `${process.env.REACT_APP_SERVER_URL}/files/${image}`;
    }

    return noImage;
  }, []);

  function handleSortProducts(): any[] {
    interface SortableProduct {
      name?: unknown;
      produto_descricao?: unknown;
      quantity?: unknown;
      total?: unknown;
      value?: unknown;
      kind?: string;
    }

    const pizzaTotals = pizzaGroupTotals(pizzas);
    const sortableProducts: SortableProduct[] = [...products];
    if (pizzaTotals.quantity > 0 || pizzas.flavors.length > 0) {
      sortableProducts.push({
        kind: 'pizzas',
        produto_descricao: 'Pizzas',
        quantity: pizzaTotals.quantity,
        total: pizzaTotals.total,
        value: pizzaTotals.quantity
          ? pizzaTotals.total / pizzaTotals.quantity
          : 0,
      });
    }

    if (productFilter === 'quantity') {
      return [...sortableProducts].sort(
        (a, b) => Number(b.quantity) - Number(a.quantity),
      );
    }

    if (productFilter === 'value') {
      const getProductTotal = (product: SortableProduct): number => {
        if (product.kind === 'pizzas') {
          const total = Number(product.total);
          return Number.isFinite(total) ? total : 0;
        }

        return productLineTotal(product);
      };

      return [...sortableProducts].sort(
        (a, b) => getProductTotal(b) - getProductTotal(a),
      );
    }

    if (productFilter === 'description') {
      return [...sortableProducts].sort((a, b) => {
        const descriptionA = String(
          a.produto_descricao ?? a.name ?? '',
        ).trim();
        const descriptionB = String(
          b.produto_descricao ?? b.name ?? '',
        ).trim();

        if (!descriptionA) return descriptionB ? 1 : 0;
        if (!descriptionB) return -1;

        return descriptionA.localeCompare(descriptionB, 'pt-BR', {
          sensitivity: 'base',
        });
      });
    }

    return sortableProducts;
  }

  const hasPermission = useCallback(
    (): boolean => findRotinas('painel_controle_detalhes')?.listar || false,
    [findRotinas],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/monitor_pedidos');
    }
  }, [addToast, hasPermission, history, rotinas]);

  const [salesChartLabels, salesChartDatasets] = handleChartData();
  const [couponChartLabels, couponChartDatasets] = handleCouponsChartData();

  function handleExportXlsx(): void {
    const startKey = getLocalDateKey(startDate);
    const endKey = getLocalDateKey(endDate);
    const dateLabel = (date: Date): string =>
      format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: pt_BR });
    const sourceNames: Record<IFilter, string> = {
      atac: 'Atac',
      ifood: 'iFood',
      pdv: 'Atac PDV',
    };

    try {
      exportDashboardDetailsWorkbook(
        buildDashboardDetailsSheets({
          periodLabel:
            startKey === endKey
              ? dateLabel(startDate)
              : `${dateLabel(startDate)} até ${dateLabel(endDate)}`,
          sources: filters.map((filter) => sourceNames[filter]),
          sections,
          stages,
          products: handleSortProducts(),
          pizzas,
          totalProducts,
          countProducts,
          complements,
          groups,
          paymentMethods,
          totalPaymentMethods,
          countPaymentMethods,
          sales: {
            title: 'Vendas',
            labels: salesChartLabels,
            datasets: salesChartDatasets,
          },
          coupons: {
            title: 'Cupons de desconto',
            labels: couponChartLabels,
            datasets: couponChartDatasets,
          },
        }),
        buildDashboardDetailsFileName(startKey, endKey),
      );
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao exportar',
        description: 'Não foi possível gerar o arquivo .xlsx.',
      });
    }
  }

  return (
    <Container>
      <Wrapper>
        <header className="wrapper-header">
          <div>
            <h1>Detalhes</h1>
            <h3>
              Período:{' '}
              {getLocalDateKey(startDate) === getLocalDateKey(endDate) ? (
                <strong>
                  {format(new Date(startDate), "dd 'de' MMMM 'de' yyyy", {
                    locale: pt_BR,
                  })}{' '}
                </strong>
              ) : (
                <>
                  de{' '}
                  <strong>
                    {format(new Date(startDate), "dd 'de' MMMM 'de' yyyy", {
                      locale: pt_BR,
                    })}{' '}
                  </strong>
                  até
                  <strong>
                    {' '}
                    {format(new Date(endDate), "dd 'de' MMMM 'de' yyyy", {
                      locale: pt_BR,
                    })}
                  </strong>
                </>
              )}
            </h3>
          </div>
          <div className="row">
            <button
              className={filters.includes('atac') ? 'filter active' : 'filter'}
              type="button"
              onClick={() => {
                setFilters((oldState) => {
                  if (oldState.includes('atac')) {
                    return oldState.filter((filter) => filter !== 'atac');
                  }

                  return [...oldState, 'atac'];
                });
              }}
            >
              <img src={AtacImage} alt="atac" />
            </button>
            <button
              className={filters.includes('ifood') ? 'filter active' : 'filter'}
              type="button"
              onClick={() => {
                setFilters((oldState) => {
                  if (oldState.includes('ifood')) {
                    return oldState.filter((filter) => filter !== 'ifood');
                  }

                  return [...oldState, 'ifood'];
                });
              }}
            >
              <img src={IfoodImage} alt="ifood" />
            </button>
            <button
              className={filters.includes('pdv') ? 'filter active' : 'filter'}
              type="button"
              onClick={() => {
                setFilters((oldState) => {
                  if (oldState.includes('pdv')) {
                    return oldState.filter((filter) => filter !== 'pdv');
                  }

                  return [...oldState, 'pdv'];
                });
              }}
            >
              <img src={PDVImage} alt="ifood" />
            </button>
            <button
              type="button"
              className="select-period"
              onClick={() => setShowCalendar((oldState) => !oldState)}
            >
              <FiCalendar />
              <span>Selecione um período</span>
            </button>
            <button
              type="button"
              className="export-xlsx"
              disabled={loading || loadError}
              onClick={handleExportXlsx}
            >
              <FiDownload />
              <span>Exportar .xlsx</span>
            </button>
          </div>
        </header>
        <Calendar
          setDate={handleChangeDate}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          hidden={!showCalendar}
        />
        {sourceStatus && filters.includes('ifood') && sourceStatus.ifood !== 'available' && sourceStatus.ifood !== 'not_selected' && (
          <p role={sourceStatus.ifood === 'error' ? 'alert' : 'status'}>
            {sourceStatus.ifood === 'not_configured'
              ? 'A integração iFood não está configurada; os gráficos iFood não incluem dados.'
              : 'Não foi possível consultar o iFood; os gráficos iFood não incluem dados deste período.'}
          </p>
        )}
        {sourceStatus && filters.includes('pdv') && sourceStatus.pdv !== 'available' && sourceStatus.pdv !== 'not_selected' && (
          <p role={sourceStatus.pdv === 'error' ? 'alert' : 'status'}>
            {sourceStatus.pdv === 'not_configured'
              ? 'A integração Atac PDV não está configurada; os gráficos Atac PDV não incluem dados.'
              : 'Não foi possível consultar o Atac PDV; os gráficos Atac PDV não incluem dados deste período.'}
          </p>
        )}
        {loading ? (
          <LoadingContainer>
            <ActivityIndicator />
          </LoadingContainer>
      ) : loadError ? (
        <LoadingContainer role="alert">
          <p>
            Não foi possível carregar os dados deste período. Tente novamente
            ao alterar os filtros.
          </p>
        </LoadingContainer>
      ) : (
          <Cards>
            <SmallCard>
              <header>
                <h3>Vendas por setor</h3>
              </header>
              <div>
                <CardItem color={theme.colors.pink}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <FaMotorcycle size={26} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>Delivery</strong>
                        <strong>
                          {Number(
                            sections.deliveredOrders.totalItems,
                          ).toLocaleString('pt-BR', BRLMoneyFormat)}
                          <Help
                            text="Taxas de entrega:"
                            value={Number(
                              sections.deliveredOrders.totalDelivery,
                            ).toLocaleString('pt-BR', BRLMoneyFormat)}
                          />
                        </strong>
                      </header>
                      <footer>
                        <span>
                          {handleNumberFormat(
                            sections.deliveredOrders.amountOfOrders,
                          )}{' '}
                          Vendas
                        </span>
                        <span>
                          {sections.count
                            ? Number(
                                (sections.deliveredOrders.amountOfOrders /
                                  sections.count) *
                                  100,
                              ).toFixed(1)
                            : 0}
                          %
                        </span>
                      </footer>
                    </div>
                  </div>
                  <ProgressBar
                    color={theme.colors.pink}
                    progress={
                      sections.count
                        ? (sections.deliveredOrders.amountOfOrders /
                            sections.count) *
                          100
                        : 0
                    }
                    className="progress-bar"
                  />
                </CardItem>
                <CardItem color={theme.colors.orange}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <MdStore size={26} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>Balcão</strong>
                        <strong>
                          {Number(
                            sections.withdrawnOrders.totalValue,
                          ).toLocaleString('pt-BR', BRLMoneyFormat)}
                        </strong>
                      </header>
                      <footer>
                        <span>
                          {handleNumberFormat(
                            sections.withdrawnOrders.amountOfOrders,
                          )}{' '}
                          Vendas
                        </span>
                        <span>
                          {sections.count
                            ? Number(
                                (sections.withdrawnOrders.amountOfOrders /
                                  sections.count) *
                                  100,
                              ).toFixed(1)
                            : 0}
                          %
                        </span>
                      </footer>
                    </div>
                  </div>
                  <ProgressBar
                    color={theme.colors.orange}
                    progress={
                      sections.count
                        ? (sections.withdrawnOrders.amountOfOrders /
                            sections.count) *
                          100
                        : 0
                    }
                    className="progress-bar"
                  />
                </CardItem>
              </div>
              <footer>
                <span>
                  <strong>{handleNumberFormat(sections.count)}</strong> Vendas
                  no total
                </span>
                <strong>
                  {Number(sections.totalWithDelivery).toLocaleString(
                    'pt-BR',
                    BRLMoneyFormat,
                  )}
                  <Help
                    text={`Total sem as taxas\n de entrega:`}
                    value={Number(sections.total).toLocaleString(
                      'pt-BR',
                      BRLMoneyFormat,
                    )}
                  />
                </strong>
              </footer>
            </SmallCard>
            <SmallCard>
              <header>
                <h3>Estágios dos pedidos</h3>
              </header>
              <div>
                <CardItem color={theme.colors.green}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <FaCheck size={20} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>Entregues</strong>
                        <span>
                          {handleNumberFormat(stages.amountOfDeliveredOrders)}{' '}
                          Pedidos
                        </span>
                      </header>
                      <ProgressBar
                        color={theme.colors.green}
                        progress={
                          stages.total
                            ? (stages.amountOfDeliveredOrders / stages.total) *
                              100
                            : 0
                        }
                        className="progress-bar"
                      />
                    </div>
                  </div>
                </CardItem>
                <CardItem color={theme.colors.orange}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <FaTimes size={22} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>Negados</strong>
                        <span>
                          {handleNumberFormat(stages.amountOfDeniedOrders)}{' '}
                          Pedidos
                        </span>
                      </header>
                      <ProgressBar
                        color={theme.colors.orange}
                        progress={
                          stages.total
                            ? (stages.amountOfDeniedOrders / stages.total) * 100
                            : 0
                        }
                        className="progress-bar"
                      />
                    </div>
                  </div>
                </CardItem>
                <CardItem color={theme.colors.red}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <FaBan size={20} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>Cancelados</strong>
                        <span>
                          {handleNumberFormat(stages.amountOfCanceledOrders)}{' '}
                          Pedidos
                        </span>
                      </header>
                      <ProgressBar
                        color={theme.colors.red}
                        progress={
                          stages.total
                            ? (stages.amountOfCanceledOrders / stages.total) *
                              100
                            : 0
                        }
                        className="progress-bar"
                      />
                    </div>
                  </div>
                </CardItem>
              </div>
            </SmallCard>
            <Card>
              <header>
                <h3>{showProducts ? 'Produtos' : 'Complementos'}</h3>
                <div>
                  {showProducts && (
                    <select
                      name="product-filter"
                      id="product-filter"
                      onChange={(event) => setProductFilter(event.target.value)}
                      defaultValue={productFilter}
                    >
                      <option value="quantity">Quantidade</option>
                      <option value="value">Total</option>
                      <option value="description">Descrição</option>
                    </select>
                  )}
                  <button
                    type="button"
                    className="change"
                    onClick={handleChangeProductsCard}
                    title={showProducts ? 'Ver complementos' : 'Ver produtos'}
                  >
                    <FaExchangeAlt size={15} />
                  </button>
                </div>
              </header>
              <main>
                {showProducts ? (
                  handleSortProducts().map((product) =>
                    product.kind === 'pizzas' ? (
                      <PizzaGroupItem
                        key="pizzas"
                        summary={pizzas}
                        countProducts={countProducts}
                        color={theme.colors.main}
                        onClick={() => pizzaModalRef.current?.open()}
                      />
                    ) : (
                      <ProductItem
                        key={`${product.produto_id ?? product.name ?? ''}-${
                          product.produto_descricao ?? ''
                        }`}
                        data={{
                          color: theme.colors.main,
                          countProducts,
                          ...product,
                        }}
                        onClick={() => {
                          if (!product.ifood) {
                            handleFetchProductComplements(
                              product.produto_controla_grade === 2
                                ? product.nome_grade_tamanho
                                : product.produto_id,
                              product.produto_controla_grade === 1,
                            );
                          }
                        }}
                      />
                    ),
                  )
                ) : (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Doughnut
                      type="doughnut"
                      redraw={false}
                      options={{
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                          tooltip: {
                            callbacks: {
                              footer: (tooltipItems: any) => {
                                return `Vendas: ${Number(
                                  tooltipItems[0].raw,
                                ).toLocaleString('pt-BR', BRLMoneyFormat)}`;
                              },
                            },
                          },
                        },
                        maintainAspectRatio: false,
                      }}
                      data={{
                        responsive: true,
                        labels: complements
                          .sort((a, b) => b.total - a.total)
                          .map(
                            (complement: any) =>
                              `${complement.nomeproduto} - ${Number(
                                Number(complement.quantity) *
                                  Number(complement.value),
                              ).toLocaleString('pt-BR', BRLMoneyFormat)}`,
                          ),
                        datasets: [
                          {
                            label: 'Vendas',
                            data: complements
                              .sort((a, b) => b.total - a.total)
                              .map((complement: any) =>
                                Number(complement.quantity),
                              ),
                            backgroundColor: colors,
                          },
                        ],
                      }}
                    />
                  </div>
                )}
              </main>
              {showProducts && (
                <footer>
                  <span>
                    <strong>{handleNumberFormat(countProducts)}</strong>{' '}
                    Produtos no total
                  </span>
                  <strong>
                    {Number(totalProducts).toLocaleString(
                      'pt-BR',
                      BRLMoneyFormat,
                    )}
                  </strong>
                </footer>
              )}
            </Card>
            <Card>
              <header>
                <h3>Vendas</h3>
                <select
                  name="sales-filter"
                  id="sales-filter"
                  onChange={(event) =>
                    setSalesFilter(
                      event.target.value as DashboardDetailsGranularity,
                    )
                  }
                  value={salesFilter}
                >
                  <option value="hour">Hora</option>
                  <option value="day">Dia</option>
                  <option value="month">Mês</option>
                </select>
              </header>
              <div
                style={{ position: 'relative', width: '100%', height: '100%' }}
              >
                <Bar
                  type="bar"
                  redraw={false}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value: number) => {
                            return Number(value).toLocaleString(
                              'pt-BR',
                              BRLMoneyFormat,
                            );
                          },
                        },
                      },
                    },
                  }}
                  data={{
                    responsive: true,
                    labels: salesChartLabels,
                    datasets: salesChartDatasets,
                  }}
                />
              </div>
              {hasAmbiguousExternalBuckets && (
                <p role="status">
                  Uma integração retornou dias ou meses sem a data completa; os
                  valores não podem ser alinhados com segurança neste período.
                </p>
              )}
            </Card>
            <Card>
              <header>
                <h3>Vendas por grupo</h3>
              </header>
              <div
                style={{ position: 'relative', width: '100%', height: '100%' }}
              >
                <Doughnut
                  type="doughnut"
                  redraw={false}
                  options={{
                    plugins: {
                      legend: {
                        position: 'right',
                      },
                      tooltip: {
                        callbacks: {
                          footer: (tooltipItems: any) => {
                            return `Total das vendas: ${Number(
                              tooltipItems[0].raw,
                            ).toLocaleString('pt-BR', BRLMoneyFormat)}`;
                          },
                        },
                      },
                    },
                    maintainAspectRatio: false,
                  }}
                  data={{
                    responsive: true,
                    labels: groups
                      .sort((a, b) => b.total - a.total)
                      .map((group: any) => `${group.descricao}`),
                    datasets: [
                      {
                        label: 'Total das vendas',
                        data: groups
                          .sort((a, b) => b.total - a.total)
                          .map((group: any) => group.total),
                        backgroundColor: colors,
                      },
                    ],
                  }}
                />
              </div>
            </Card>
            <Card>
              <header>
                <h3>Finalizadoras</h3>
              </header>
              <main>
                {paymentMethods
                  .sort((a, b) => b.count - a.count)
                  .map((paymentMethod) => {
                    if (paymentMethod.ifood) {
                      return (
                        <CardItem key={paymentMethod.name}>
                          <div className="wrapper">
                            <div className="icon-container">
                              <img
                                src={handleImage(paymentMethod.image)}
                                alt={paymentMethod.name}
                              />
                            </div>
                            <div className="info">
                              <header>
                                <div style={{ textAlign: 'left' }}>
                                  <strong>{paymentMethod.name}</strong>
                                  <img
                                    src={IfoodImage}
                                    alt="ifood"
                                    style={{ marginLeft: 10, marginTop: 7 }}
                                  />
                                </div>
                                <strong>
                                  {Number(paymentMethod.total).toLocaleString(
                                    'pt-BR',
                                    BRLMoneyFormat,
                                  )}
                                </strong>
                              </header>
                              <footer>
                                <span>
                                  {handleNumberFormat(paymentMethod.quantity)}{' '}
                                  Finalizações
                                </span>
                                <span>
                                  {Number(
                                    (paymentMethod.quantity /
                                      countPaymentMethods) *
                                      100,
                                  ).toFixed(1)}
                                  %{' '}
                                </span>
                              </footer>
                            </div>
                          </div>
                          <ProgressBar
                            color={theme.colors.main}
                            progress={
                              (paymentMethod.quantity / countPaymentMethods) *
                              100
                            }
                            className="progress-bar"
                          />
                        </CardItem>
                      );
                    }

                    return (
                      <CardItem key={paymentMethod.forma_pagamento_empresa_id}>
                        <div className="wrapper">
                          <div className="icon-container">
                            <img
                              src={handleImage(
                                paymentMethod.forma_pagamento_imagem,
                              )}
                              alt={paymentMethod.forma_pagamento_descricao}
                            />
                          </div>
                          <div className="info">
                            <header>
                              <strong>
                                {paymentMethod.forma_pagamento_descricao}
                              </strong>
                              <strong>
                                {Number(paymentMethod.sum).toLocaleString(
                                  'pt-BR',
                                  BRLMoneyFormat,
                                )}
                              </strong>
                            </header>
                            <footer>
                              <span>
                                {handleNumberFormat(paymentMethod.count)}{' '}
                                Finalizações
                              </span>
                              <span>
                                {Number(
                                  (paymentMethod.count / countPaymentMethods) *
                                    100,
                                ).toFixed(1)}
                                %{' '}
                              </span>
                            </footer>
                          </div>
                        </div>
                        <ProgressBar
                          color={theme.colors.main}
                          progress={
                            (paymentMethod.count / countPaymentMethods) * 100
                          }
                          className="progress-bar"
                        />
                      </CardItem>
                    );
                  })}
              </main>
              <footer>
                <span>
                  <strong>{handleNumberFormat(countPaymentMethods)}</strong>{' '}
                  Finalizações no total
                </span>
                <strong>
                  {Number(totalPaymentMethods).toLocaleString(
                    'pt-BR',
                    BRLMoneyFormat,
                  )}
                </strong>
              </footer>
            </Card>
            <Card>
              <header>
                <h3>Cupons de desconto</h3>
                <select
                  name="coupon-filter"
                  id="coupon-filter"
                  onChange={(event) =>
                    setCouponFilter(
                      event.target.value as DashboardDetailsGranularity,
                    )
                  }
                  value={couponFilter}
                >
                  <option value="hour">Hora</option>
                  <option value="day">Dia</option>
                  <option value="month">Mês</option>
                </select>
              </header>
              <div
                style={{ position: 'relative', width: '100%', height: '100%' }}
              >
                <Bar
                  type="bar"
                  redraw={false}
                  options={{
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: {
                          callback: (value: number) => {
                            return Number(value).toLocaleString(
                              'pt-BR',
                              BRLMoneyFormat,
                            );
                          },
                        },
                      },
                      x: {
                        stacked: true,
                      },
                    },
                  }}
                  data={{
                    responsive: true,
                    labels: couponChartLabels,
                    datasets: couponChartDatasets,
                  }}
                />
              </div>
            </Card>
          </Cards>
        )}
      </Wrapper>
      <PizzaDetailsModal
        ref={pizzaModalRef}
        summary={pizzas}
        atacDetails={{}}
        detailsVersion={0}
        onSelectSize={() => undefined}
      />
      <Modal ref={complementsModalRef}>
        {productComplements?.flavors && (
          <div style={{ marginBottom: 10 }}>
            <header>
              <h3>Sabores</h3>
              <button type="button" onClick={handleCloseComplementsModal}>
                <FiX />
              </button>
            </header>
            <main>
              {productComplements?.flavors &&
                productComplements.flavors
                  .sort((a, b) => Number(b.quantity) - Number(a.quantity))
                  .map((complement) => (
                    <CardItem key={complement.id} color={theme.colors.main}>
                      <div className="wrapper">
                        <div className="icon-container">
                          <FaPizzaSlice size={26} />
                        </div>
                        <div className="info">
                          <header>
                            <strong>{complement.nomeproduto}</strong>
                            <strong>
                              {Number(
                                Number(complement.quantity) *
                                  Number(complement.value),
                              ).toLocaleString('pt-BR', BRLMoneyFormat)}
                            </strong>
                          </header>
                          <footer>
                            <span>
                              Quantidade: {Number(complement.quantity)}
                            </span>
                            <span>
                              {productComplements?.flavors &&
                                Number(
                                  (complement.quantity /
                                    productComplements.flavors.reduce(
                                      (
                                        previousValue: number,
                                        currentValue: any,
                                      ) =>
                                        previousValue +
                                        Number(currentValue.quantity),
                                      0,
                                    )) *
                                    100,
                                ).toFixed(1)}
                              %
                            </span>
                          </footer>
                        </div>
                      </div>
                      <ProgressBar
                        color={theme.colors.main}
                        progress={
                          productComplements?.flavors
                            ? (complement.quantity /
                                productComplements.flavors.reduce(
                                  (previousValue: number, currentValue: any) =>
                                    previousValue +
                                    Number(currentValue.quantity),
                                  0,
                                )) *
                              100
                            : 0
                        }
                        className="progress-bar"
                      />
                    </CardItem>
                  ))}
            </main>
          </div>
        )}
        {productComplements?.grids && (
          <div style={{ marginBottom: 10 }}>
            <header>
              <h3>Grades</h3>
              <button type="button" onClick={handleCloseComplementsModal}>
                <FiX />
              </button>
            </header>
            <main>
              {productComplements?.grids &&
                productComplements.grids
                  .sort((a, b) => Number(b.quantity) - Number(a.quantity))
                  .map((grid) => (
                    <CardItem key={grid.id} color={theme.colors.main}>
                      <div className="wrapper">
                        <div className="icon-container">
                          <BiGridAlt size={26} />
                        </div>
                        <div className="info">
                          <header>
                            <strong>{grid.nomeproduto}</strong>
                            <strong>
                              {Number(
                                Number(grid.quantity) * Number(grid.value),
                              ).toLocaleString('pt-BR', BRLMoneyFormat)}
                            </strong>
                          </header>
                          <footer>
                            <span>Quantidade: {Number(grid.quantity)}</span>
                            <span>
                              {productComplements?.grids &&
                                Number(
                                  (grid.quantity /
                                    productComplements.grids.reduce(
                                      (
                                        previousValue: number,
                                        currentValue: any,
                                      ) =>
                                        previousValue +
                                        Number(currentValue.quantity),
                                      0,
                                    )) *
                                    100,
                                ).toFixed(1)}
                              %
                            </span>
                          </footer>
                        </div>
                      </div>
                      <ProgressBar
                        color={theme.colors.main}
                        progress={
                          productComplements?.grids
                            ? (grid.quantity /
                                productComplements.grids.reduce(
                                  (previousValue: number, currentValue: any) =>
                                    previousValue +
                                    Number(currentValue.quantity),
                                  0,
                                )) *
                              100
                            : 0
                        }
                        className="progress-bar"
                      />
                    </CardItem>
                  ))}
            </main>
          </div>
        )}
        <header>
          <h3>Complementos</h3>
          {!productComplements?.flavors && !productComplements?.grids && (
            <button type="button" onClick={handleCloseComplementsModal}>
              <FiX />
            </button>
          )}
        </header>
        <main>
          {loadingComplements ? (
            <LoadingContainer>
              <ActivityIndicator />
            </LoadingContainer>
          ) : (
            productComplements?.complements &&
            productComplements.complements
              .sort((a, b) => Number(b.quantity) - Number(a.quantity))
              .map((complement) => (
                <CardItem key={complement.id} color={theme.colors.main}>
                  <div className="wrapper">
                    <div className="icon-container">
                      <FaUtensils size={26} />
                    </div>
                    <div className="info">
                      <header>
                        <strong>{complement.nomeproduto}</strong>
                        <strong>
                          {Number(
                            Number(complement.quantity) *
                              Number(complement.value),
                          ).toLocaleString('pt-BR', BRLMoneyFormat)}
                        </strong>
                      </header>
                      <footer>
                        <span>
                          {handleNumberFormat(Number(complement.quantity))}{' '}
                          Vendas
                        </span>
                        <span>
                          {productComplements?.complements &&
                            Number(
                              (complement.quantity /
                                productComplements.complements.reduce(
                                  (previousValue: number, currentValue: any) =>
                                    previousValue +
                                    Number(currentValue.quantity),
                                  0,
                                )) *
                                100,
                            ).toFixed(1)}
                          %
                        </span>
                      </footer>
                    </div>
                  </div>
                  <ProgressBar
                    color={theme.colors.main}
                    progress={
                      productComplements?.complements
                        ? (complement.quantity /
                            productComplements.complements.reduce(
                              (previousValue: number, currentValue: any) =>
                                previousValue + Number(currentValue.quantity),
                              0,
                            )) *
                          100
                        : 0
                    }
                    className="progress-bar"
                  />
                </CardItem>
              ))
          )}
        </main>
        <footer>
          <button
            className="default"
            type="button"
            onClick={handleCloseComplementsModal}
          >
            <span>Pronto</span>
          </button>
        </footer>
      </Modal>
    </Container>
  );
};

export default DashboardDetails;
